import { CheckedAddress } from '@sparkdotfi/common-universal'

import {
  AccountDefinition,
  AccountSparkPointsSummary,
  AccountSparkRewardsSummary,
  InterestData,
  MigrationInfo,
} from '../types'
import { getAccountMetadata } from '../utils/getAccountMetadata'
import { getChainConfigEntry } from '@/config/chain'
import { sortByUsdValueWithUsdsPriority } from '@/domain/common/sorters'
import { TokenRepository } from '@/domain/token-repository/TokenRepository'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { calculateMaxBalanceTokenAndTotal } from '@/features/savings/logic/calculateMaxBalanceTokenAndTotal'
import { SavingsAccount } from '@/features/savings/logic/savings-converters/types'
import { getAccountDisplayedUnderlyingToken } from '@/features/savings/utils/getAccountDisplayedUnderlyingToken'

export interface MakeAccountDefinitionParams {
  selectedAccount: SavingsAccount
  interestData: InterestData
  sparkRewardsSummary: AccountSparkRewardsSummary
  sparkPointsSummary: AccountSparkPointsSummary
  selectedSavingsTokenSymbol: TokenSymbol
  getBlockExplorerLink: (address: CheckedAddress) => string | undefined
  chainId: number
  savingsTokenRepository: TokenRepository

  migrationInfo?: MigrationInfo
}
export function makeAccountDefinition({
  selectedAccount,
  interestData,
  selectedSavingsTokenSymbol,
  chainId,
  getBlockExplorerLink,
  migrationInfo,
  sparkRewardsSummary,
  sparkPointsSummary,
  savingsTokenRepository,
}: MakeAccountDefinitionParams): AccountDefinition {
  const { savings } = getChainConfigEntry(chainId)
  const psmStables = savings?.psmStables

  const selectedAccountConfig = savings?.accounts?.find(
    ({ savingsToken }) => savingsToken === selectedSavingsTokenSymbol,
  )
  const savingsTokenBalance = savingsTokenRepository.findOneBalanceOrZero(selectedSavingsTokenSymbol)
  const supportedAssetsWithBalance = (selectedAccountConfig?.supportedAssets ?? []).map((symbol) =>
    savingsTokenRepository.findOneBySymbol(symbol),
  )

  const { maxBalanceToken } = calculateMaxBalanceTokenAndTotal({
    assets: supportedAssetsWithBalance,
    tokenRepository: savingsTokenRepository,
  })
  const sortedSupportedAssets = sortByUsdValueWithUsdsPriority(supportedAssetsWithBalance, savingsTokenRepository).map(
    (token) => ({
      token,
      balance: savingsTokenRepository.findOneBalanceOrZero(token.symbol),
      blockExplorerLink: getBlockExplorerLink(token.address),
    }),
  )

  const displayedUnderlyingTokenDefinition = getAccountDisplayedUnderlyingToken({
    account: selectedAccount,
    chainId,
  })
  const displayedUnderlyingToken = savingsTokenRepository.findOneBySymbol(displayedUnderlyingTokenDefinition.symbol)
  const savingsToken = savingsTokenRepository.findOneBySymbol(selectedAccount.savingsToken.symbol)

  const hideSendDialogButton = selectedSavingsTokenSymbol === TokenSymbol('spETH')
  const showConvertDialogButton = Boolean(
    psmStables && psmStables.length > 1 && import.meta.env.VITE_BASE_MINI_APP === '1',
  )

  return {
    interestData,
    savingsToken,
    savingsTokenBalance,
    underlyingToken: displayedUnderlyingToken,
    supportedAssets: sortedSupportedAssets,
    mostValuableAsset: maxBalanceToken,
    showConvertDialogButton,
    migrationInfo,
    metadata: getAccountMetadata({
      savingsToken: selectedSavingsTokenSymbol,
      underlyingToken: selectedAccount.underlyingToken.symbol,
      chainId,
    }),
    sparkRewardsSummary,
    sparkPointsSummary,
    hideSendDialogButton,
  }
}
